/* eslint-disable no-console */
import { supabase } from '@/integrations/supabase/client';

// Enhanced origin validation
const ALLOWED_ORIGINS = [
  'https://arendapro.plus',
  'https://www.arendapro.plus',
  'http://localhost:5173',
  'http://localhost:3000',
  'https://localhost:5173',
  'https://localhost:3000'
];

export const validateOrigin = (origin: string): boolean => {
  if (import.meta.env.DEV) {
    return origin.includes('localhost') || origin.includes('127.0.0.1');
  }
  return ALLOWED_ORIGINS.includes(origin);
};

// Список популярных/слабых паролей для защиты от dictionary attacks
const COMMON_PASSWORDS = [
  'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
  'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'password1',
  'qwerty123', 'dragon', 'master', 'hello', 'freedom', 'whatever',
  'qwertyuiop', 'trustno1', 'starwars', 'football', 'baseball',
  'superman', 'iloveyou', 'batman', 'sunshine', 'princess', 'lovely',
  'flower', 'charlie', 'shadow', 'michael', 'jordan', 'tigger',
  'mustang', 'access', 'computer', 'diamond', 'killer', 'summer',
  'internet', 'service', 'canada', 'hello123', 'hunter', 'banana',
  'test', 'testing', 'guest', 'user', 'root', 'administrator'
];

// Enhanced input validation
export const validateInput = {
  email: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  },

  password: (password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    // Проверка на популярные/слабые пароли (защита от dictionary attacks)
    const lowerPassword = password.toLowerCase();
    if (COMMON_PASSWORDS.includes(lowerPassword)) {
      errors.push('This password is too common. Please choose a more unique password');
    }

    // Дополнительная проверка на простые паттерны
    if (/^(.)\1+$/.test(password)) {
      errors.push('Password cannot consist of repeated characters');
    }

    if (/^(123|abc|qwe)/i.test(password) || /(123|abc|qwe)$/i.test(password)) {
      errors.push('Password cannot start or end with common sequences (123, abc, qwe)');
    }

    return { valid: errors.length === 0, errors };
  },

  phone: (phone: string): boolean => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  },

  uuid: (uuid: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }
};

// Secure session management
export const sessionSecurity = {
  async validateSession(): Promise<boolean> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        return false;
      }

      // Check if session is expired
      const now = Math.floor(Date.now() / 1000);
      if (session.expires_at && session.expires_at < now) {
        await supabase.auth.signOut();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  },

  async refreshSession(): Promise<boolean> {
    try {
      const { error } = await supabase.auth.refreshSession();
      return !error;
    } catch (error) {
      console.error('Session refresh error:', error);
      return false;
    }
  }
};

// Enhanced security headers with strengthened CSP
export const securityHeaders = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'DENY',
  'X-XSS-Protection': '1; mode=block',
  'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload',
  'Content-Security-Policy': [
    "default-src 'self'",
    "script-src 'self' 'wasm-unsafe-eval'", // Убираем 'unsafe-inline', добавляем 'wasm-unsafe-eval' для Vite
    "style-src 'self' 'unsafe-inline'", // Пока оставляем для Tailwind, но можно заменить на nonce
    "img-src 'self' data: https:",
    "connect-src 'self' https://aoucmkkqfbqccmcerwtr.supabase.co https://*.supabase.co wss://*.supabase.co",
    "font-src 'self' data:",
    "object-src 'none'", // Блокируем object/embed для безопасности
    "media-src 'self'",
    "frame-src 'none'", // Блокируем iframe для безопасности
    "base-uri 'self'", // Ограничиваем base URI
    "form-action 'self'", // Ограничиваем отправку форм
    "frame-ancestors 'none'", // Защита от clickjacking
    "upgrade-insecure-requests" // Автоматическое обновление HTTP до HTTPS
  ].join('; '),
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=(), payment=(), usb=()'
};

// Data sanitization
export const sanitizeData = {
  string: (input: string): string => {
    return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                .replace(/javascript:/gi, '')
                .replace(/on\w+\s*=/gi, '')
                .trim();
  },

  number: (input: unknown): number | null => {
    const num = typeof input === 'number' ? input : Number(input as number | string);
    return isNaN(num) ? null : num;
  },

  boolean: (input: unknown): boolean => {
    return Boolean(input);
  }
};

// Унификация ошибок аутентификации для защиты от enumeration attacks
export const unifyAuthError = (error: unknown): string => {
  if (!error) {
    return 'Неверные учетные данные. Проверьте email и пароль.';
  }

  const errorMessage = typeof error === 'string' ? error : (error as { message?: string } | null)?.message || '';
  const lowerError = errorMessage.toLowerCase();

  // Список ошибок, которые могут раскрывать информацию о существовании пользователей
  const sensitiveErrors = [
    'user not found',
    'invalid login credentials',
    'email not confirmed',
    'account not found',
    'user does not exist',
    'invalid email or password',
    'email not found',
    'password is incorrect',
    'wrong password',
    'user not registered',
    'account does not exist',
    'email is not registered'
  ];

  // Проверяем, содержит ли ошибка чувствительную информацию
  const isSensitiveError = sensitiveErrors.some(sensitiveError => 
    lowerError.includes(sensitiveError)
  );

  if (isSensitiveError) {
    // Возвращаем унифицированное сообщение, не раскрывающее информацию
    return 'Неверные учетные данные. Проверьте email и пароль.';
  }

  // Для rate limiting и других технических ошибок возвращаем оригинальное сообщение
  if (lowerError.includes('too many') || lowerError.includes('rate limit')) {
    return errorMessage;
  }

  // Для всех остальных ошибок возвращаем общее сообщение
  return 'Произошла ошибка при входе. Попробуйте позже.';
};
